<script>
export default {
	name: 'ContactUs',
	inheritAttrs: false,
};
</script>

<script setup>
import FormWrapper from '~/components/form/FormWrapper.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import Dropdown from '~/components/form/Dropdown.vue';
import FileUpload from '~/components/form/FileUpload.vue';
import TextField from '~/components/form/TextField.vue';
import DatePicker from '~/components/form/DatePicker.vue';
import CTAButton from '~/aem-components/CTAButton.vue';
import TextArea from '~/components/form/MHTextArea.vue';
import RecaptchaGoogle from '~/components/form/RecaptchaGoogle.vue';
import RecaptchaMT from '~/components/form/RecaptchaMT.vue';
import AppSpinner from '~/components/AppSpinner.vue';
import { ref } from 'vue';
import yup from 'mh-yup';
import axios from 'axios';

const props = defineProps({
	generalTabDetails: {
		type: Object, default: () => ({
			contactName: 'Contact Name',
			email: 'Email',
			countryCode: 'Country Code',
			phone: 'Phone',
			subject: 'Subject',
			description: 'Description',
			type: 'Type',
			oneworldMembershipTier: 'Membership Tier',
			airlineName: 'Airline Name',
			specifyOtherAirline: 'Specify other Airlines',
			area: 'Area',
			category: 'Category',
			flightDate: 'Flight Date',
			flightDestination: 'Flight Destination',
			flightNumber: 'Flight Number',
			flightOrigin: 'Flight Origin',
			ticketNumber: 'Ticket Number',
			seatNumber: 'Seat Number',
			class: 'Class',
			baggageReportNo: 'Baggage Report Number',
			enrichNumber: 'Enrich Number',
			enrichTier: 'Enrich Tier',
			successMessage: '',
			errorMessage: '',
			fileUploadToolTipMessage: '',
			flightNumberFormatText: '',
			recaptchaType: 'google-recaptcha',
			formType: 'gts',
			referenceNumber: 'PNR (reference number)',
			fileUploadLabel: 'File upload',
			fileUploadCount: '5',
			phoneNumberFormatText: '',
			ticketNumberFormatText: '',
			seatNumberFormatText: '',
			baggageReportNumberFormatText: '',
			enrichNumberFormatText: '',
			isGTSNewSalesforceApi: false,
			isFireflyNewSalesforceApi: false,
		}),
	},
	dropdownTabDetails: {
		type: Object, default: () => ({}),
	},
	allowedExtensions: { type: String, default: 'JPEG, JPG, PNG, PDF' },
	uploadButtonLabel: { type: String, default: 'Drag and drop File' },
	fileFormatSupportedLabel: { type: String, default: 'Supported File Types:' },
});
const contactFormData = ref({
	contactName: '',
	email: '',
	countryCode: '',
	phoneNumber: '',
	phone: '',
	subject: '',
	description: '',
	type: '',
	oneworldMembershipTier: '',
	airlineName: '',
	otherAirlineName: '',
	area: '',
	category: '',
	flightDestination: '',
	flightNumber: '',
	flightDate: ref(null),
	flightOrigin: '',
	seatNumber: '',
	cabinClass: '',
	ticketNumber: '',
	baggageReport: '',
	enrichNumber: '',
	enrichTier: '',
	fileUploadObj: '',
	pnrNumber: '',
	enabledForm: '',
});
const isLoading = ref(false);
const isError = ref(null);
const isSuccess = ref(null);
const recaptchaGoogleEl = ref(null);
const recaptchaMTEl = ref(null);
const categoryList = ref([]);
const isTokenError = ref(false);
const siteKey = props.generalTabDetails.recaptchaType === 'google-recaptcha'
	? '6LdhpyklAAAAAOkdkdtcREPPMjxdRuZJcldEK_y9'
	: 'MTPublic-77FVOz99s';

const mapDetailsToComputedList = (details) => {
	const type = details.dataSource === 'generic-list' ? [...details.genericList] : [...details.multiList];
	return type.map((item) => ({
		'label': item.text,
		'value': item.value,
	}));
};

const organizeAreaAndCategory = (details) => {
	const area = details.dataSource === 'generic-list' ? [...details.genericList] : [...details.multiList];
	const organizedArea = area.map((item) => {
		const [label, value] = item.text.split(',');
		const categoryOptions = item.value.split(',');
		return {
			'label': label,
			'value': value,
			'category': categoryOptions,
		};
	});
	return organizedArea;
};

const countryCodeList = computed(() => mapDetailsToComputedList(props.dropdownTabDetails.countryCodeDetails));
const typeList = computed(() => mapDetailsToComputedList(props.dropdownTabDetails.typeDetails));
const membershipTierList = computed(() => mapDetailsToComputedList(props.dropdownTabDetails.oneWorldMembershipDetails));
const airlineList = computed(() => mapDetailsToComputedList(props.dropdownTabDetails.airlineNameDetails));
const destinationList = computed(() => mapDetailsToComputedList(props.dropdownTabDetails.flightDestinationDetails));
const originList = computed(() => mapDetailsToComputedList(props.dropdownTabDetails.flightOriginDetails));
const cabinClassList = computed(() => mapDetailsToComputedList(props.dropdownTabDetails.contactFormClassDetails));
const enrichTierList = computed(() => mapDetailsToComputedList(props.dropdownTabDetails.enrichTierDetails));

const areaList = computed(() => organizeAreaAndCategory(props.dropdownTabDetails.areaDetails));

const validateFeildValues = (value, fieldName) => {
	if (value === null) {
		return true;
	} else if (fieldName === 'phone') {
		let regx = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;
		let isCountryCodeFilled = contactFormData.value.countryCode && contactFormData.value.countryCode.trim() !== '';
		return value.match(regx) && isCountryCodeFilled;
	} else if (fieldName === 'flightNumber') {
		return isFlightNumberValid(value);
	} else if (fieldName === 'pnr') {
		return value.length === 6;
	} else if (fieldName === 'ticketNumber') {
		return isTicketNoValid(value);
	} else if (fieldName === 'seatNumber') {
		return isSeatNoValid(value);
	} else if (fieldName === 'enrichNumber') {
		return isEnrichNumber(value);
	} else if (fieldName === 'baggageReport') {
		return isBaggageReportNo(value);
	}
};
const isFlightNumberValid = (flightNumber) => {
	if (flightNumber.length !== 6 ||
		(flightNumber.match(/[a-zA-Z]/g) || []).length !== 2 ||
		(flightNumber.match(/\d/g) || []).length !== 4) {
		return false;
	}
	return true;
};
const isTicketNoValid = (ticketNumber) => {
	if (ticketNumber.length !== 13 || !/^\d+$/.test(ticketNumber)) {
		return false;
	}
	return true;
};
const isSeatNoValid = (seatNo) => {
	if (seatNo.length !== 3 || (seatNo.match(/[a-zA-Z]/g) || []).length !== 1 || (seatNo.match(/\d/g) || []).length !== 2) {
		return false;
	}
	return true;
};
const isEnrichNumber = (enrichNo) => {
	if (enrichNo.length !== 9 || !/^[0-9]+$/.test(enrichNo)) {
		return false;
	}
	return true;
};
const isBaggageReportNo = (baggageReportNo) => {
	if (baggageReportNo.length > 25 || !/((^[0-9]+[a-z]+)|(^[a-z]+[0-9]+))+$/i.test(baggageReportNo)) {
		return false;
	}
	return true;
};

async function getFormRecaptchaToken () {
	let token = null;

	switch (props.generalTabDetails.recaptchaType) {
		case 'google-recaptcha':
			if (recaptchaGoogleEl.value?.isReady) {
				token = await recaptchaGoogleEl.value.getRecaptchaToken();
			}
			break;

		case 'mt-recaptcha':
			if (recaptchaMTEl.value?.isReady) {
				token = recaptchaMTEl.value.getRecaptchaToken();
			}
			break;
	
		default:
			break;
	}

	return token;
}
const handleAirlinesConfiguration = (value) => {
	if (value !== 'Others') {
		contactFormData.value.otherAirlineName = '';
	}
};
const submitForm = async () => {
	isLoading.value = true;
	isSuccess.value = false;
	isError.value = false;
	// Recaptcha token
	const token = await getFormRecaptchaToken();
	if (!token) {
		isTokenError.value = true;
		isLoading.value = false;
		return;
	}
	isTokenError.value = false;
	if (contactFormData.value.phoneNumber) {
		const code = contactFormData.value.countryCode.replace(/^\+/, '');
		contactFormData.value.phone = `${code}${contactFormData.value.phoneNumber}`;
	}
	if (contactFormData.value.flightDate === null) {
		contactFormData.value.flightDate = '';
	}

	if (props.generalTabDetails.isGTSNewSalesforceApi === 'true' || props.generalTabDetails.isFireflyNewSalesforceApi === 'true') {
		contactFormData.value.enabledForm =
			props.generalTabDetails.isGTSNewSalesforceApi === 'true' ?
				'gts-new-salesforce-2024' :
				'firefly-new-salesforce-2024';
	} else {
		contactFormData.value.enabledForm = props.generalTabDetails.formType;
	}
	
	contactFormData.value.fileUploadLimit = props.generalTabDetails.fileUploadCount;
	const formData = new FormData();
	const fileUploadObj = contactFormData.value.fileUploadObj;
	// Loop through the fileUploadObj keys and append them to formData
	Object.keys(fileUploadObj).forEach((key, index) => {
		if (index < fileUploadObj.length) {
			contactFormData.value[`uploadedFile${index + 1}`] = fileUploadObj[key];
		}
	});
	// Loop through contactFormData.value keys and append them to formData
	for (const key in contactFormData.value) {
		if (Object.prototype.hasOwnProperty.call(contactFormData.value, key)
			&& (key !== 'phoneNumber' && key !== 'fileUploadObj')) {
			formData.append(key, contactFormData.value[key]);
		}
	}
	axios.post('/bin/mh/revamp/gts/formsubmission', formData)
		.then((response) => {
			isSuccess.value = true;
			isError.value = false;
			isLoading.value = false;
		})
		.catch((error) => {
			isSuccess.value = false;
			isError.value = true;
			isLoading.value = false;
		});
};
const handleCategoryOptions = (selectedValue) => {
	if (contactFormData.value.category) {
		contactFormData.value.category = null;
	}
	const selectedIndex = areaList.value.findIndex((area) => area.value === selectedValue);
	categoryList.value = areaList.value[selectedIndex].category.map((category) => {
		return {
			label: category,
			value: category,
		};
	});
};
</script>


<template>
<div class="ContactUS-GTS  w-full justify-center items-center m-auto >lg:w-1/2 p-4">
	<FormWrapper v-if="!isSuccess" :focusFirstErrorElOnInvalidSubmit="true" @submit-valid="submitForm">
		<TextField
			:modelValue="contactFormData.contactName"
			:validation="yup.string().required('Contact name is required')"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="contact-name"
			:ariaLabel="props.generalTabDetails.contactName"
			autocomplete="off"
			required
			@update:model-value="contactFormData.contactName = $event"
		/>
		<TextField
			:modelValue="contactFormData.email"
			:validation="yup.string().email('An email address format is username@domain.com').required('Email is required')"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="email"
			:ariaLabel="props.generalTabDetails.email"
			autocomplete="off"
			required
			@update:model-value="contactFormData.email = $event"
		/>
		<Dropdown
			:modelValue="contactFormData.countryCode"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="country-code"
			:options="countryCodeList"
			:ariaLabel="props.generalTabDetails.countryCode"
			:labelText="props.generalTabDetails.countryCode"
			:isSearchable="true"
			@update:model-value="contactFormData.countryCode = $event"
		>
		</Dropdown>
		<TextField
			:modelValue="contactFormData.phoneNumber"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="phone"
			:ariaLabel="props.generalTabDetails.phone"
			autocomplete="off"
			:validation="yup
				.string()
				.test(
					'validate-phone',
					'Invalid format. Please refer to the example',
					(value) => validateFeildValues(value, 'phone')
				)"
			@update:model-value="contactFormData.phoneNumber = $event"
		/>
		<div class="imp-note-for-flight-no mb-5 text-left">
			<p v-html-sanitize="props.generalTabDetails.phoneNumberFormatText"></p>
		</div>
		<TextField
			:modelValue="contactFormData.subject"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="subject"
			:ariaLabel="props.generalTabDetails.subject"
			:validation="yup.string().required()"
			autocomplete="off"
			required
			@update:model-value="contactFormData.subject = $event"
		/>
		<TextArea
			:modelValue="contactFormData.description"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="description"
			:ariaLabel="props.generalTabDetails.description"
			:validation="yup.string().required()"
			autocomplete="off"
			@update:model-value="contactFormData.description = $event"
		></TextArea>
		<Dropdown
			:modelValue="contactFormData.type"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="type"
			:options="typeList"
			:ariaLabel="props.generalTabDetails.type"
			:labelText="props.generalTabDetails.type"
			:isSearchable="true"
			required
			@update:model-value="contactFormData.type = $event"
		>
		</Dropdown>
		<Dropdown
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.oneworldMembershipTier"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="membership-tier"
			:options="membershipTierList"
			:ariaLabel="props.generalTabDetails.oneworldMembershipTier"
			:labelText="props.generalTabDetails.oneworldMembershipTier"
			:isSearchable="true"
			@update:model-value="contactFormData.oneworldMembershipTier = $event"
		>
		</Dropdown>
		<Dropdown
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.airlineName"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="airlineName"
			:options="airlineList"
			:ariaLabel="props.generalTabDetails.airlineName"
			:labelText="props.generalTabDetails.airlineName"
			:isSearchable="true"
			required
			@update:model-value="contactFormData.airlineName = $event"
			@change="handleAirlinesConfiguration($event)"
		>
		</Dropdown>
		<TextField
			v-if="contactFormData.airlineName === 'Others' && props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.otherAirlineName"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="other-airline"
			:ariaLabel="props.generalTabDetails.specifyOtherAirline"
			:validation="contactFormData.airlineName === 'Others' ? yup.string().required() : null"
			:required="contactFormData.airlineName === 'Others'"
			autocomplete="off"
			@update:model-value="contactFormData.otherAirlineName = $event"
		/>
		<Dropdown
			:modelValue="contactFormData.area"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="area"
			:options="areaList"
			:ariaLabel="props.generalTabDetails.area"
			:labelText="props.generalTabDetails.area"
			:isSearchable="true"
			required
			:clearOnSelect="true"
			@update:model-value="contactFormData.area = $event"
			@change="handleCategoryOptions($event)"
		>
		</Dropdown>
		<Dropdown
			:modelValue="contactFormData.category"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="category"
			:options="categoryList"
			:ariaLabel="props.generalTabDetails.category"
			:labelText="props.generalTabDetails.category"
			:isSearchable="true"
			required
			@update:model-value="contactFormData.category = $event"
		>
		</Dropdown>
		<TextField
			v-if="props.generalTabDetails.formType === 'firefly'"
			:modelValue="contactFormData.pnrNumber"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="referenceNumber"
			:ariaLabel="props.generalTabDetails.referenceNumber"
			:validation="yup
				.string()
				.test(
					'validate-pnr',
					'Maximum 6 characters allowed',
					(value) => validateFeildValues(value, 'pnr')
				)"
			autocomplete="off"
			@update:model-value="contactFormData.pnrNumber = $event"
		/>
		<DatePicker
			:modelValue="contactFormData.flightDate"
			:rootAttrs="{
				class: 'mb-5',
			}"
			name="flight-date"
			:ariaLabel="props.generalTabDetails.flightDate"
			:labelText="props.generalTabDetails.flightDate"
			class="form-element"
			:displayYear="['t-120', 't+10']"
			variant="mhe"
			@update:model-value="contactFormData.flightDate = $event"
		/>
		<Dropdown
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.flightDestination"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="flight-destination"
			:options="destinationList"
			:ariaLabel="props.generalTabDetails.flightDestination"
			:labelText="props.generalTabDetails.flightDestination"
			:isSearchable="true"
			@update:model-value="contactFormData.flightDestination = $event"
		>
		</Dropdown>
		<TextField
			:modelValue="contactFormData.flightNumber"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="flight-number"
			:ariaLabel="props.generalTabDetails.flightNumber"
			:validation="yup
				.string()
				.test(
					'validate-pnr',
					'Invalid format. Please refer to the example',
					(value) => validateFeildValues(value, 'flightNumber')
				)"
			autocomplete="off"
			@update:model-value="contactFormData.flightNumber = $event"
		/>
		<div class="imp-note-for-flight-no mb-5 text-left">
			<p v-html-sanitize="props.generalTabDetails.flightNumberFormatText"></p>
		</div>
		<Dropdown
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.flightOrigin"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="flight-origin"
			:options="originList"
			:ariaLabel="props.generalTabDetails.flightOrigin"
			:labelText="props.generalTabDetails.flightOrigin"
			:isSearchable="true"
			@update:model-value="contactFormData.flightOrigin = $event"
		>
		</Dropdown>
		<TextField
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.ticketNumber"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="ticketno"
			:ariaLabel="props.generalTabDetails.ticketNumber"
			:validation="yup
				.string()
				.test(
					'validate-ticket-number',
					'Invalid format. Please refer to the example',
					(value) => validateFeildValues(value, 'ticketNumber')
				)"
			autocomplete="off"
			@update:model-value="contactFormData.ticketNumber = $event"
		/>
		<div
			v-if="props.generalTabDetails.formType === 'gts'"
			class="imp-note-for-flight-no mb-5 text-left"
		>
			<p v-html-sanitize="props.generalTabDetails.ticketNumberFormatText"></p>
		</div>
		<TextField
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.seatNumber"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="seatno"
			:ariaLabel="props.generalTabDetails.seatNumber"
			:validation="yup
				.string()
				.test(
					'validate-seat-number',
					'Invalid format. Please refer to the example.',
					(value) => validateFeildValues(value, 'seatNumber')
				)"
			autocomplete="off"
			@update:model-value="contactFormData.seatNumber = $event"
		/>
		<div
			v-if="props.generalTabDetails.formType === 'gts'"
			class="imp-note-for-flight-no mb-5 text-left"
		>
			<p v-html-sanitize="props.generalTabDetails.seatNumberFormatText"></p>
		</div>
		<Dropdown
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.cabinClass"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="class"
			:options="cabinClassList"
			:ariaLabel="props.generalTabDetails.class"
			:labelText="props.generalTabDetails.className"
			:isSearchable="true"
			@update:model-value="contactFormData.cabinClass = $event"
		>
		</Dropdown>
		<TextField
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.baggageReport"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="baggage-report-number"
			:validation="yup
				.string()
				.test(
					'validate-baggage-report',
					'Invalid format. Please refer to the example.',
					(value) => validateFeildValues(value, 'baggageReport')
				)"
			:ariaLabel="props.generalTabDetails.baggageReportNo"
			autocomplete="off"
			@update:model-value="contactFormData.baggageReport = $event"
		/>
		<div
			v-if="props.generalTabDetails.formType === 'gts'"
			class="imp-note-for-flight-no mb-5 text-left"
		>
			<p v-html-sanitize="props.generalTabDetails.baggageReportNumberFormatText"></p>
		</div>
		<TextField
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelValue="contactFormData.enrichNumber"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="enrich-no"
			:ariaLabel="props.generalTabDetails.enrichNumber"
			:labelText="props.generalTabDetails.enrichNumber"
			:validation="yup
				.string()
				.test(
					'validate-enrich-number',
					'Invalid format. Please refer to the example..',
					(value) => validateFeildValues(value, 'enrichNumber')
				)"
			autocomplete="off"
			@update:model-value="contactFormData.enrichNumber = $event"
		/>
		<div
			v-if="props.generalTabDetails.formType === 'gts'"
			class="imp-note-for-flight-no mb-5 text-left"
		>
			<p v-html-sanitize="props.generalTabDetails.enrichNumberFormatText"></p>
		</div>
		<Dropdown
			v-if="props.generalTabDetails.formType === 'gts'"
			:modelvalue="contactFormData.enrichTier"
			:rootAttrs="{
				class: 'mb-5',
			}"
			class="form-element"
			name="enrich-tier"
			:options="enrichTierList"
			:ariaLabel="props.generalTabDetails.enrichTier"
			:labelText="props.generalTabDetails.enrichTier"
			:isSearchable="true"
			@update:model-value="contactFormData.enrichTier = $event"
		>
		</Dropdown>
		<div v-html-sanitize="props.generalTabDetails.fileUploadLabel"></div>
		<FileUpload
			:modelValue="contactFormData.fileUploadObj"
			name="upload-file1"
			ariaLabel="file"
			class="form-element w-full"
			:class="{ 'md:w-40': !isMobileViewport }"
			:listAllowedFileExtensions="props.allowedExtensions"
			:desktopUploadButtonLabel="props.uploadButtonLabel"
			:maximumNoAttachmentLimit="props.generalTabDetails.fileUploadCount"
			maxNoAttachmentLimitLabel="Maximum attachment allowed"
			@update:model-value="contactFormData.fileUploadObj = $event"
		>
			<template #tooltip-content>{{ props.generalTabDetails.fileUploadToolTipMessage }}</template>
			<template #max-limit-msg>Maximum {{ props.generalTabDetails.fileUploadCount }} attachment allowed.</template>
		</FileUpload>
		<div>
			<RecaptchaGoogle v-if="props.generalTabDetails.recaptchaType === 'google-recaptcha'" ref="recaptchaGoogleEl" :siteKey="siteKey" required />
			<RecaptchaMT v-if="props.generalTabDetails.recaptchaType === 'mt-recaptcha'" ref="recaptchaMTEl" :siteKey="siteKey" required />
		</div>
		<div v-if="isTokenError" class="errorMessage-container text-semantic-red-base">Failed to get captcha token</div>
		<div v-if="isError" class="errorMessage-container text-semantic-red-base"><p v-html-sanitize="props.generalTabDetails.errorMessage"></p></div>
		<div v-if="isLoading" class="my-auto pb-20 md:pt-12 md:pb-11">
			<AppSpinner class="mx-auto my-auto" />
		</div>
		<CTAButton
			v-if="!isLoading"
			type="submit"
			alignmentDesktop="center"
			alignmentMobile="center"
			:ctaSizing="isMobileViewport ? 'fluid' : ''"
		>
			Submit
		</CTAButton>
	</FormWrapper>
	<div v-else class="success"><p v-html-sanitize="props.generalTabDetails.successMessage"></p></div>
</div>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';


.ContactUS-GTS {

	.success {
		color: var.$semantic-green-base;
		font-size: medium;
		font-weight: bold;
	}
}

</style>
